import React, { useState, useEffect, createRef, useContext } from 'react';
import { Badge, Button, Card, Container, Col, Row, Spinner } from 'react-bootstrap';
import { FiRefreshCw } from 'react-icons/fi';
import { FaCloud, FaTrashAlt, FaRegThumbsUp, FaPlug, FaEdit } from 'react-icons/fa';
import { BiError } from 'react-icons/bi';
import MaterialTable, { MTableToolbar } from 'material-table';
import icons from 'MaterialTableIcons';
import AppContext from 'Utils/AppContext';
import { getErrorMessage } from 'Utils';
import { INK, STATUS } from 'Utils/styleTokens';
import PageHeader from 'Utils/PageHeader';
import DiscoveryTooltip from 'DiscoveryTooltip';
import {
  listCloudAccounts,
  createCloudAccount,
  updateCloudAccount,
  deleteCloudAccount,
  testCloudAccount
} from './api';
import CloudAccountFormModal from './CloudAccountFormModal';

const AUTH_MODE_LABELS = {
  SERVICE_PRINCIPAL: 'Service principal',
  ACCESS_KEY: 'Access key'
};

const columns = [
  { title: 'Name', field: 'displayName' },
  {
    title: 'Cloud',
    field: 'cloud',
    render: (row) => (
      <Badge variant={row.cloud === 'AWS' ? 'warning' : 'info'} className='pl-3 pr-3 pt-2 pb-2'>
        <span className='text-uppercase'>{row.cloud}</span>
      </Badge>
    )
  },
  { title: 'Identifier', field: 'accountIdentifier' },
  {
    title: 'Auth mode',
    field: 'authMode',
    render: (row) => AUTH_MODE_LABELS[row.authMode] || row.authMode
  },
  {
    title: 'Enabled',
    field: 'enabled',
    render: (row) => (row.enabled ? 'Yes' : 'No')
  }
];

const CloudAccounts = () => {
  const [fetching, setFetching] = useState(false);
  const [data, setData] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [editing, setEditing] = useState(null);
  const [formError, setFormError] = useState('');
  const { setNotification, deploymentCloud } = useContext(AppContext);
  const tableRef = createRef();

  const load = async () => {
    setFetching(true);
    try {
      const rows = await listCloudAccounts();
      setData(Array.isArray(rows) ? rows : []);
    } catch (e) {
      setNotification({
        header: 'Could not load alternate credentials',
        icon: <BiError size='25px' color={INK[500]} />,
        body: <span>{getErrorMessage(e)}</span>
      });
    }
    setFetching(false);
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const openAdd = () => {
    setEditing(null);
    setFormError('');
    setShowForm(true);
  };

  const openEdit = (account) => {
    setEditing(account);
    setFormError('');
    setShowForm(true);
  };

  const closeForm = () => {
    setShowForm(false);
    setEditing(null);
    setFormError('');
  };

  const onSave = async (payload) => {
    try {
      if (editing) {
        await updateCloudAccount(editing.id, payload);
      } else {
        await createCloudAccount(payload);
      }
      await load();
      setShowForm(false);
      setEditing(null);
      setNotification({
        header: editing ? 'Alternate credential updated' : 'Alternate credential added',
        icon: <FaRegThumbsUp size='25px' color={STATUS.success.dot} />,
        body: <span>Saved successfully.</span>
      });
    } catch (e) {
      setFormError(getErrorMessage(e));
    }
  };

  const onDelete = async (account) => {
    if (!window.confirm(`Delete alternate credential "${account.displayName}"?`)) return;
    try {
      await deleteCloudAccount(account.id);
      await load();
      setNotification({
        header: 'Alternate credential deleted',
        icon: <FaRegThumbsUp size='25px' color={STATUS.success.dot} />,
        body: <span>Deleted {account.displayName}.</span>
      });
    } catch (e) {
      setNotification({
        header: 'Delete failed',
        icon: <BiError size='25px' color={INK[500]} />,
        body: <span>{getErrorMessage(e)}</span>
      });
    }
  };

  const onTest = async (account) => {
    setNotification({
      header: 'Testing connection…',
      icon: <FaPlug size='25px' color={INK[500]} />,
      body: <span>Contacting c2-manager to probe {account.displayName}…</span>
    });
    try {
      const result = await testCloudAccount(account.id);
      if (result && result.success) {
        setNotification({
          header: 'Credentials verified',
          icon: <FaRegThumbsUp size='25px' color={STATUS.success.dot} />,
          body: (
            <span>
              <div><strong>{account.displayName}</strong></div>
              <div>Source: {result.credentialSource || 'unknown'}</div>
              {result.message && <div className='text-muted'>{result.message}</div>}
            </span>
          )
        });
      } else {
        setNotification({
          header: 'Credential probe failed',
          icon: <BiError size='25px' color={INK[500]} />,
          body: <span>{(result && result.message) || 'Unknown error.'}</span>
        });
      }
    } catch (e) {
      setNotification({
        header: 'Credential probe failed',
        icon: <BiError size='25px' color={INK[500]} />,
        body: <span>{getErrorMessage(e)}</span>
      });
    }
  };

  return (
    <Col className='p-0'>
      <Container fluid>
        <Container>
          <PageHeader
            header='Alternate Credentials'
            icon={FaCloud}
            tooltip='Fallback stored credentials for data sources the platform workload identity cannot reach. Most deployments never need one.'
          />
          <Row>
            <Col>
              <Card>
                <MaterialTable
                  isLoading={fetching}
                  tableRef={tableRef}
                  actions={[{}]}
                  columns={columns}
                  components={{
                    Toolbar: (props) => (
                      <div className='entity-panel-table-header d-flex'>
                        <div className='text-left widget-header p-3'>
                          <FaCloud className='mr-2 widget-header-icon' />
                          <span>Registered alternate credentials</span>
                        </div>
                        <div className='d-flex flex-grow-1 justify-content-end'>
                          <MTableToolbar {...props} />
                          <Button
                            onClick={load}
                            className='ml-0 mr-2 pl-4 pr-4'
                            variant='outline-secondary'
                            disabled={fetching}
                            type='button'
                          >
                            {fetching ? (
                              <Spinner className='mr-2' as='span' animation='border' size='sm' role='status' aria-hidden='true' />
                            ) : (
                              <FiRefreshCw className='mr-2' />
                            )}
                            REFRESH
                          </Button>
                          <Button
                            onClick={openAdd}
                            className='ml-0 mr-0 pl-4 pr-4'
                            variant='primary'
                            type='button'
                          >
                            + ADD
                          </Button>
                        </div>
                      </div>
                    ),
                    Action: (p) => (
                      <div>
                        <DiscoveryTooltip text='Test connection' placement='top'>
                          <FaPlug
                            size='1rem'
                            data-testid='cloud-account-test'
                            onClick={() => onTest(p.data)}
                            className='ml-2 mr-2 admin-icon'
                          />
                        </DiscoveryTooltip>
                        <DiscoveryTooltip text='Edit' placement='top'>
                          <FaEdit
                            size='1rem'
                            data-testid='cloud-account-edit'
                            onClick={() => openEdit(p.data)}
                            className='ml-2 mr-2 admin-icon'
                          />
                        </DiscoveryTooltip>
                        <DiscoveryTooltip text='Delete' placement='top'>
                          <FaTrashAlt
                            className='ml-2 mr-2 trash-icon'
                            data-testid='cloud-account-delete'
                            onClick={() => onDelete(p.data)}
                          />
                        </DiscoveryTooltip>
                      </div>
                    )
                  }}
                  data={data}
                  icons={icons}
                  localization={{ header: { actions: 'ACTIONS' } }}
                  options={{
                    actionsColumnIndex: -1,
                    showTitle: false,
                    search: true,
                    pageSize: 10
                  }}
                />
              </Card>
            </Col>
          </Row>
          <CloudAccountFormModal
            show={showForm}
            account={editing}
            error={formError}
            onClose={closeForm}
            onSave={onSave}
            deploymentCloud={deploymentCloud}
          />
        </Container>
      </Container>
    </Col>
  );
};

export default CloudAccounts;
